import { useEffect, useMemo, useRef, useState } from "react";
import { Formik } from "formik";
import Button from "../../../../../../components/Button";
import Slider from "react-slick";
import Modal from "../../../../../../components/Modal";
import * as Yup from "yup";
import Approved from "./components/Approved";
import Disapproved from "./components/Disapproved";
import {
  Title,
  Quiz,
  Steps,
  Step,
  ProgressBar,
  ProgressBarWhite,
  Question,
  Answer,
  Radio,
  Checkbox,
  Content,
  Actions,
  Prev,
  Next,
  ErrorMessage,
  Submit,
} from "./styles";
import ArrowRight from "../../../../../../assets/icons/icon-cool/outline/Arrows-chevron/Right.svg";
import ArrowLeft from "../../../../../../assets/icons/icon-cool/outline/Arrows-chevron/Left.svg";
import { connect, useDispatch } from "react-redux";
import {
  getOneQuiz,
  newAnswerQuiz,
  resetNewAnswerQuiz,
} from "../../../../../../store/actions/quiz";
import { QuizInDto } from "../../../../../../types/quiz.dto";
import Icon from "../../../../../../components/Icon";
import slugify from "slugify";
import { hideModal, showModal } from "../../../../../../store/actions/modal";
import { useNavigate } from "react-router-dom";
import { useLayout } from "../../../../../../provider/LayoutProvider";
import { updateOneEnrollment } from "../../../../../../store/actions/enrollment";
import { Action, Dispatch } from "redux";
import useIsMobile from "../../../../../../hooks/deviceChecker";
import useCapitalLetter from "../../../../../../hooks/useCapitalLetter.ts";
import { CourseInDto } from "../../../../../../types/course.dto";

interface ComponentProps {
  enrollment;
  currentUnit;
  currentComponent;
  idEnrollment: number;
  idQuiz: number;
  course: CourseInDto;
  quiz: QuizInDto;
  quizStates: {
    loading: boolean;
    success: boolean;
    error: string | boolean;
  };
  newAnswersQuiz;
  newAnswersQuizStates: {
    loading: boolean;
    success: boolean;
    error: string | boolean;
  };
  isLastCourseUnitComponent;
  nextComponentUrl;
  previousComponentUrl: string;
}

const validationSchema = Yup.object().shape({
  enrollment_id: Yup.number().required("Enrollment ID is required"),
  answers: Yup.array().of(
    Yup.object().shape({
      option_id: Yup.array()
        .of(Yup.number().required("Please select an option"))
        .min(1),
      question_id: Yup.number().required(),
    })
  ),
});

interface initialValuesDto {
  enrollment_id?: number;
  answers: { option_id: number[]; question_id: number }[];
}

const Component = ({
  course,
  idEnrollment,
  idQuiz,
  quiz,
  quizStates,
  newAnswersQuiz,
  newAnswersQuizStates,
  isLastCourseUnitComponent,
  nextComponentUrl,
  enrollment,
  currentUnit,
  currentComponent,
  previousComponentUrl
}: ComponentProps) => {
  const isMobile = useIsMobile();
  const capitalize = useCapitalLetter();
  const dispatch = useDispatch<Dispatch<Action>>();
  const navigate = useNavigate();
  const [showError, setShowError] = useState(false);
  const [formSubmmited, setFormSubmmited] = useState<boolean>(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  const { setIsPageLoading } = useLayout();

  const initialValues = useMemo(
    (): initialValuesDto => ({
      enrollment_id: idEnrollment, // Initialize with an empty string if idEnrollment is not available
      answers:
        quiz?.labQuizQuestion?.map((question, questionIndex) => ({
          option_id: [], // Initialize as an empty array
          question_id: question?.id, // Convert to string as per the validation schema
        })) || [],
    }),
    [idEnrollment, quiz]
  );

  let sliderRef = useRef<Slider>(null);

  const next = () => {
    if (sliderRef.current) {
      sliderRef.current.slickNext();
    }
  };

  const previous = () => {
    if (sliderRef.current) {
      sliderRef.current.slickPrev();
    }
  };

  const quitTest = () => {
    dispatch(showModal("quit-quiz"));
  }

  const onFormSubmit = ({ values, actions }) => {
    dispatch(newAnswerQuiz(values));
  };

  var settings = useMemo(
    () => ({
      dots: false,
      infinite: false,
      speed: 500,
      slidesToShow: 1,
      slidesToScroll: 1,
      swipe: false,
      swipeToSlide: false,
      touchMove: false,
      draggable: false,
      adaptiveHeight: false,
      beforeChange: (_oldIndex, current: number) =>
        setCurrentQuestionIndex(current),
    }),
    [quiz]
  );

  useEffect(() => {
    if (!!idQuiz) {
      dispatch(getOneQuiz({ idQuiz }));
    }
  }, [idQuiz]);

  useEffect(() => {
    if (newAnswersQuiz && newAnswersQuizStates.success) {
      dispatch(hideModal());

      if (newAnswersQuiz?.score >= 75 && isLastCourseUnitComponent) {
        dispatch(
          updateOneEnrollment({
            idEnrollment: idEnrollment,
            body: {
              state: "Finished",
              completed_date: new Date(),
              components_completed: course?.total_components,
            },
          })
        );
      }
    }
  }, [newAnswersQuizStates, newAnswersQuiz, course]);

  useEffect(() => {
    if (!quizStates || quizStates.loading) {
      setIsPageLoading(true);
    } else {
      setIsPageLoading(false);
    }
  }, [quizStates]);

  useEffect(() => {
    dispatch(resetNewAnswerQuiz());
  }, [])

  return (
    <>
      <Quiz>
        <Content>
          {newAnswersQuiz?.score >= 75 && (
            <Approved
              isLastCourseUnitComponent={isLastCourseUnitComponent}
              nextComponentUrl={nextComponentUrl}
              enrollment={enrollment}
              currentUnit={currentUnit}
              currentComponent={currentComponent}
            />
          )}

          {newAnswersQuiz?.score < 75 && <Disapproved />}

          {!newAnswersQuiz && !!quiz && (
            <>
              <Title>Test</Title>

              <p>
                {`Pregunta ${currentQuestionIndex + 1}/${quiz?.labQuizQuestion.length
                  }`}
              </p>
              {
                quiz?.labQuizQuestion.length > 1 ?
                  <Steps>
                    {quiz?.labQuizQuestion?.map((question, index) => {
                      const completed = index < currentQuestionIndex;
                      const isActive = index === currentQuestionIndex;

                      return (
                        <Step
                          key={slugify(`${question.id}-${question.question_text}`, {
                            lower: true,
                          })}
                          completed={completed}
                          active={isActive}
                        />
                      );
                    })}
                    <ProgressBar completionPercentage={(currentQuestionIndex / (quiz?.labQuizQuestion?.length - 1) || 0) * 100} />
                    <ProgressBarWhite />
                  </Steps>
                  : null
              }

              <div style={{ height: 24 }} />

              <Formik
                initialValues={initialValues}
                onSubmit={(values, actions) => {
                  if (onFormSubmit) onFormSubmit({ values, actions });
                }}
                validateOnChange={formSubmmited}
                validateOnBlur={false}
                validationSchema={validationSchema}
                enableReinitialize
              >
                {({
                  touched,
                  errors,
                  values,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                }) => {
                  return (
                    <form
                      onSubmit={(event) => {
                        setFormSubmmited(true);
                        handleSubmit(event);
                      }}
                      id="quiz-form"
                      style={{ display: "block" }}
                    >
                      <Slider {...settings} ref={sliderRef}>
                        {quiz?.labQuizQuestion?.map((question, questionIndex) => {
                          return (
                            <div>
                              <Question>{question.question_text}</Question>

                              <div style={{ height: 34 }} />

                              {question?.labQuizOption.map(
                                (answer, answerIndex) => {
                                  return (
                                    <div
                                      key={slugify(
                                        `${answer.id}-${answer.option_text}`,
                                        {
                                          lower: true,
                                        }
                                      )}
                                    >
                                      <Answer>
                                        <span>{capitalize(answer?.option_text)}</span>
                                        {question?.type_quiz === "MULTI" && (
                                          <Checkbox
                                            type="checkbox"
                                            name={`answers.${questionIndex}.option_id`}
                                            value={answer?.id}
                                            onChange={(e) => {
                                              const selectedValue = Number(
                                                e.target.value
                                              ); // Convert to number
                                              const isChecked = e.target.checked;
                                              const currentValues =
                                                values.answers[questionIndex]
                                                  ?.option_id || [];
                                              let updatedValues;

                                              if (isChecked) {
                                                updatedValues = [
                                                  ...currentValues,
                                                  selectedValue,
                                                ]; // Add to array
                                              } else {
                                                updatedValues =
                                                  currentValues.filter(
                                                    (val) => val !== selectedValue
                                                  ); // Remove from array
                                              }

                                              handleChange({
                                                target: {
                                                  name: `answers.${questionIndex}.option_id`,
                                                  value: updatedValues,
                                                },
                                              });
                                            }}
                                          />
                                        )}
                                        {question?.type_quiz === "SIMPLE" && (
                                          <Radio
                                            type="radio"
                                            name={`answers.${questionIndex}.option_id`}
                                            value={answer?.id}
                                            onChange={(e) => {
                                              const selectedValue =
                                                e.target.value;
                                              handleChange({
                                                target: {
                                                  name: `answers.${questionIndex}.option_id`,
                                                  value: [Number(selectedValue)], // Convert to array
                                                },
                                              });
                                            }}
                                          />
                                        )}
                                        {question?.type_quiz === "TORF" && (
                                          <Radio
                                            type="radio"
                                            name={`answers.${questionIndex}.option_id`}
                                            value={answer?.id}
                                            onChange={(e) => {
                                              const selectedValue =
                                                e.target.value;
                                              handleChange({
                                                target: {
                                                  name: `answers.${questionIndex}.option_id`,
                                                  value: [Number(selectedValue)], // Convert to array
                                                },
                                              });
                                            }}
                                          />
                                        )}
                                      </Answer>

                                      {answerIndex + 1 <
                                        question?.labQuizOption.length && (
                                          <div style={{ height: 10 }} />
                                        )}
                                    </div>
                                  );
                                }
                              )}
                            </div>
                          );
                        })}
                      </Slider>

                      <div style={{ height: 30 }} />

                      {showError && (
                        <ErrorMessage>
                          Por favor seleccione una opción para completar la
                          pregunta
                        </ErrorMessage>
                      )}

                      <div style={{ height: 30 }} />

                      <Actions>

                        <Button
                          type="button"
                          onClick={() => {
                            if (currentQuestionIndex > 0) {
                              setShowError(false);
                              previous();
                            } else {
                              quitTest();
                            }
                          }}
                          disabled={newAnswersQuizStates.loading}
                          options={{ skin: "gray", size: "sm", type: "outline" }}
                        >
                          <Icon icon={ArrowLeft} size="16px" color={"black"} />{" "}
                          {currentQuestionIndex > 0 ? 'Volver' : 'Salir del Test'}
                        </Button>

                        {currentQuestionIndex <
                          quiz?.labQuizQuestion?.length - 1 && (
                            <Button
                              type="button"
                              onClick={() => {
                                const isValid =
                                  !!values.answers?.[currentQuestionIndex]
                                    ?.option_id &&
                                  values.answers?.[currentQuestionIndex]?.option_id
                                    ?.length > 0;

                                setShowError(!isValid);
                                isValid && next();
                              }}
                              options={{
                                skin: "primary",
                                size: "sm",
                                type: "filled",
                              }}
                            >
                              Siguiente{" "}
                              <Icon icon={ArrowRight} size="16px" color={"white"} />
                            </Button>
                          )}

                        {currentQuestionIndex ===
                          quiz?.labQuizQuestion?.length - 1 && (
                            <Next
                              onClick={(event) => {
                                const isValid =
                                  !!values.answers?.[currentQuestionIndex]
                                    ?.option_id &&
                                  values.answers?.[currentQuestionIndex]?.option_id
                                    ?.length > 0;

                                setShowError(!isValid);
                                if (isValid) {
                                  dispatch(showModal("confirm-quiz-submit"));
                                }
                              }}
                              type="button"
                            >
                              Finalizar
                            </Next>
                          )}
                      </Actions>
                    </form>
                  );
                }}
              </Formik>
            </>
          )}

          <div style={{ height: 34 }} />
        </Content>

        <Modal
          width={isMobile ? "100%" : "50%"}
          center={true}
          title="Finalizar Test"
          name="confirm-quiz-submit"
        >
          <p style={{ textAlign: 'center' }}>
            ¿Está seguro que desea terminar el intento? En caso de avanzar y no
            alcanzar el porcentaje necesario de aprobación deberá volver a
            realizarlo.
          </p>
          <div style={{ height: 20 }} />
          <Actions>
            <Button
              type="button"
              onClick={() => {
                dispatch(hideModal());
              }}
              disabled={newAnswersQuizStates.loading}
              options={{ skin: "gray", size: "sm", type: "outline" }}
            >
              Cancelar
            </Button>

            <Submit
              type="submit"
              form="quiz-form"
              isSubmmiting={newAnswersQuizStates.loading}
            >
              Finalizar
            </Submit>
          </Actions>
        </Modal>

      </Quiz>
      <Modal
        width={isMobile ? "100%" : "50%"}
        center={true}
        title="Salir del Test"
        name="quit-quiz"
      >
        <p style={{ textAlign: 'center' }}>¿Desea salir del Test? Se perderá la información del mismo</p>
        <div style={{ height: 20 }} />
        <Actions>
          <Button
            type="button"
            onClick={() => {
              dispatch(hideModal());
            }}
            disabled={newAnswersQuizStates.loading}
            options={{ skin: "gray", size: "sm", type: "outline" }}
          >
            Cancelar
          </Button>

          <Button
            type="button"
            onClick={() => {
              dispatch(hideModal());
              navigate(previousComponentUrl);
            }}
            disabled={newAnswersQuizStates.loading}
            options={{ skin: "primary", size: "sm", type: "filled" }}
          >
            Salir
          </Button>
        </Actions>
      </Modal>
    </>
  );
};

const state = ({ quizStore, courseStore }) => {
  const { data: quiz, states: quizStates } = quizStore.getOne;
  const { data: newAnswersQuiz, states: newAnswersQuizStates } =
    quizStore.newAnswersQuiz;

  const { data: course, states: courseStates } = courseStore.getOne;

  return {
    course,
    courseStates,
    quiz,
    quizStates,
    newAnswersQuiz,
    newAnswersQuizStates,
  };
};

export default connect(state)(Component);
